import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { unwrapResult } from '@reduxjs/toolkit';
import { TableBody as Body, Tooltip } from '@mui/material';

import { CustomAlert, TableNoData, Text } from '../../shared/uiComponents';
import { HeaderProperties } from '../../shared/uiComponents/Table/tableProperties';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import { errorNormalizer } from '../../shared/Helpers/functions';
import { EditSvg, Info } from '../../shared/Assets/svgIcons';
import Delete from '../../components/Delete';

import { DispatchProperties } from '../../redux/store';
import { ExtendedRoleProperties } from '../../redux/API/identityAPIProperties';
import { deleteRole, getRoles } from '../../redux/State/identitySlice/roleSlice';

interface RowRendererProperties {
	data: ExtendedRoleProperties[] | null;
}

export const Headers: HeaderProperties[] = [
	{ id: '8', name: 'Role name' },
	{ id: '1', name: 'Usage' },
	{ id: '2', name: 'Created' },
	{ id: '3', name: 'Last Updated' },
	{ id: '4', name: 'Actions' },
];

export const RoleInfo = ({ role }: { role: ExtendedRoleProperties }) => {
	const [open, setOpen] = useState<boolean>(false);
	const { claims } = role;

	return (
		<>
			<div onClick={() => setOpen(true)}>
				<Tooltip title="More Information">
					<span>
						<Info />
					</span>
				</Tooltip>
			</div>
			<CustomAlert
				open={open}
				onClose={() => setOpen(false)}
				title="Accesses:"
				Content={() => (
					<>
						{claims.map((claim, index) => (
							<Text key={index} title={claim.displayName as string} type="psm" fontWeight="semiBold" />
						))}
					</>
				)}
			/>
		</>
	);
};

const Actions = ({ row }: { row: ExtendedRoleProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const deleteAdminHandler = (id: string | undefined) => {
		if (!id) return;
		dispatch(deleteRole(id))
			.then(unwrapResult)
			.then(() => {
				dispatch(getRoles());
				toast(`Deleted`);
			})
			.catch(errorNormalizer);
	};

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				cursor: 'pointer',
				gap: 16,
				justifyContent: 'flex-end',
			}}
		>
			{!row.isReadonly && (
				<>
					<Link to={`/permissions/${row.id}`}>
						<Tooltip title="Edit">
							<span>
								<EditSvg />
							</span>
						</Tooltip>
					</Link>
					<Tooltip title="Delete">
						<span>
							<Delete deleteHandler={() => deleteAdminHandler(row.id)} />
						</span>
					</Tooltip>
				</>
			)}
			<RoleInfo role={row} />
		</div>
	);
};

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	if (!data?.length) {
		return <TableNoData spanScope={Headers.length} />;
	}

	return (
		<Body>
			{data.map((row, index) => (
				<CustomTableRow key={index}>
					<RowTableCell>{row.name}</RowTableCell>
					<RowTableCell>{row.numberOfUsage}</RowTableCell>
					<RowTableCell>{dayjs(row.createdAt).format('MM-DD-YYYY')}</RowTableCell>
					<RowTableCell>{row.modifiedAt ? dayjs(row.modifiedAt).format('MM-DD-YYYY') : '-'}</RowTableCell>
					<RowTableCell>
						<Actions row={row} />
					</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};
